import {
  calculateMinPossibleProcessingAmount,
  calculateMaxPossibleProcessingAmount,
} from 'utils/helper-functions/limits-calculation';
import { applyFailedChargeCap } from '@pano-project/utils/getFailedChargeCap';
import { calculateFee } from 'utils/fee-functions/calculate-comission';
import { round } from 'utils/round-functions/round';
import { getWalletCurrencyLimits } from 'utils/hooks/digital-wallets/helpers';
import type { GatewayLimits } from '@pano-project/utils/getGatewayLimits';
import store from '../../state/store';

// Without a conversion the retry limits are stated before the fee. The input holds the total, so
// each limit becomes a total with the same fee call that builds the stored total of a choice
// (getRetryAmountsFromOffer): the limit plus its fee for a fee on top, unchanged for a fee included.
const limitToTotal = (
  limit: number | null | undefined,
  gateway: Record<string, any>,
  currency: string,
  transactionType: string,
) => {
  if (limit === null || limit === undefined) return null;
  if (!Number(limit)) return limit;
  const { total } = calculateFee(gateway.fees || {}, Number(limit), currency, transactionType);
  const totalNumber = Number(total);
  return round(isNaN(totalNumber) ? Number(limit) : totalNumber, currency);
};

const NO_LIMITS: GatewayLimits = { min: null, max: null, hasLimits: false };

// The limits of the Panoramic classic retry input (the total to charge in the processing
// currency). The hint, the range check and the main amount check read this one rule.
// The currency is an argument on purpose: the stored page currency is still empty when the
// main check runs on first load.
export const getClassicRetryLimits = (
  currency: string | null | undefined,
  gateway?: Record<string, any>,
): GatewayLimits => {
  if (!currency || !gateway) return NO_LIMITS;

  const state = store.getState();
  const baseCurrency = state.profileInfo?.base_currency;
  const transactionType = state.profileInfo?.transaction_type;
  const resultOfPay = state.resultOfPay;

  let min: number | null | undefined;
  let max: number | null | undefined;

  if (currency !== baseCurrency) {
    min = calculateMinPossibleProcessingAmount(currency, gateway);
    max = calculateMaxPossibleProcessingAmount(currency, gateway);
  } else {
    const limits =
      getWalletCurrencyLimits(currency) ?? gateway.payment_information?.currency_list?.[currency];
    min = limitToTotal(limits?.min_amount, gateway, currency, transactionType);
    max = limitToTotal(limits?.max_amount, gateway, currency, transactionType);
  }

  max = applyFailedChargeCap(max, currency, resultOfPay);

  const hasMin = min !== null && min !== undefined;
  const hasMax = max !== null && max !== undefined;
  return {
    min: hasMin ? (min as number) : null,
    max: hasMax ? (max as number) : null,
    hasLimits: hasMin && hasMax,
  };
};
